import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { PermissionService } from '@app/core/auth/services/permission.service';
import { ChannelService } from '@app/core/channels/services/channel.service';
import { RfqService } from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { ShippingCarrierService } from '@app/core/shipping-carriers/services/shipping-carrier.service';

@Component({
  selector: 'cnc-inline-margin-calculator',
  templateUrl: './inline-margin-calculator.component.html',
  styleUrls: ['./inline-margin-calculator.component.scss'],
})
export class InlineMarginCalculatorComponent
  implements OnInit, OnChanges, OnDestroy
{
  settings :any;

  shippingWeightPrices :any[] = [];
  channels :any[] = [];
  totalCost :number = 0;

  fees :any[] = [];
  marginPercent :number = 0;
  marginAmount :number = 0;
  overheadCost :number = 0;
  defaultMaterialCost :any = 'computed_weighted';
  incOverheadCost :boolean = false;

  shippingWeight :any = '';
  packageCost :number = 0;
  laborCost :number = 0;
  returnDefectiveCost :number = 0;

  private onDestroy$ = new Subject();

  subCh :Subscription;
  subSc :Subscription;
  subSettings :Subscription;

  // models
  @Input() rfq :any;

  constructor(
    public rfqSvc :RfqService,
    private cd :ChangeDetectorRef,
    private shippingCarrierSvc :ShippingCarrierService,
    private settingsSvc :SettingsService,
    public channelSvc :ChannelService,
    public UserPermission :PermissionService,
  ) {}

  ngOnInit() {
    this.setRfqActivePrice().subscribe();
    this.setRfqBasePrice().subscribe();
    // get channels
    this.channelSvc.get({ all: 1 });
    this.subCh = this.channelSvc.channels$.subscribe((channels) => {
      if (channels && channels.length > 0) {
        this.channels = channels;
        this.setRfq();
        this.cd.detectChanges();
      }
    });
    this.subSc = this.shippingCarrierSvc.getAllAsync().subscribe((resp :any) => {
      this.shippingWeightPrices = resp.data;
      this.setRfq();
      this.cd.detectChanges();
    });
    this.subSettings = this.settingsSvc.settings$.subscribe((settings :any) => {
      this.settings = settings;
      if (this.settings && this.settings.overhead_cost) {
        this.overheadCost = this.settings.overhead_cost;
        this.defaultMaterialCost = this.settings.default_material_cost;
      }
      this.setRfq();
      this.cd.detectChanges();
    });

    this.initSummaryValues();
  }

  ngOnChanges(changes :SimpleChanges) :void {
    this.setRfq();
  }

  ngOnDestroy() :void {
    this.subCh.unsubscribe();
    this.subSc.unsubscribe();
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    this.subSettings.unsubscribe();
  }

  initSummaryValues() :void {
    this.shippingWeight = this.getShippingWeight(this.rfq);
    this.packageCost = this.getPackageCost(this.rfq);
    this.laborCost = this.getLaborCost(this.rfq);
    this.returnDefectiveCost = this.getReturnDefectiveCost(this.rfq);
  }

  private setRfqActivePrice() :Observable<any> {
    return this.rfqSvc.getActiveRetailPricesById(this.rfq?.id).pipe(
      tap((response :any) => {
        this.rfq.RfqActivePrices = response;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private setRfqBasePrice() :Observable<any> {
    return this.rfqSvc.getBasePrices(this.rfq.id).pipe(
      tap((response :any) => {
        this.rfq.RfqBasePrices = response;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  setRfq() :void {
    if (
      !this.rfq ||
      !this.rfq.id ||
      !this.rfq.RfqBasePrices ||
      !this.rfq.RfqActivePrices
    ) {
      return;
    }
    this.rfq.unit_costs = this.rfqSvc.getSkuUnitCosts(this.rfq);
    this.rfq.unit_costs.total_cost_defective_products = 0;
    this.rfq.unit_costs.total_item_shipped = 0;

    // get stocks material cost manually set
    Object.keys(this.rfq.unit_costs).forEach((key) => {
      if (!this.rfq.unit_costs[key] || this.rfq.unit_costs[key] == 0) {
        this.rfq.unit_costs[key] = this.rfq.RfqBasePrices[key.toLocaleLowerCase()];
      }
    });

    try {
      this.rfq.shipping_weight = JSON.parse(this.rfq?.shipping_weight);
      this.rfq.product_weight = JSON.parse(this.rfq?.product_weight);
    } catch (e) {}

    this.channels.forEach((channel :any) => {
      channel.shippingPaid = this.getShippingPaid(this.rfq, channel);
      channel.retailPrice = this.getChannelRetailPrice(this.rfq, channel);
      channel.shippingCarrierId = this.getChannelShippingCarrierId(
        this.rfq,
        channel,
      );
      channel.materialCost = this.getChannelMaterialCost(this.rfq, channel);
      this.computeChannelMargin(channel);
    });
  }

  computeChannelMargin(channel :any) :void {
    this.fees = [];
    this.marginPercent = 0;
    this.marginAmount = 0;
    this.totalCost = 0;

    const shippingCost = this.getShippingCost(this.rfq, channel);
    const tempMaterialCost = this.getMaterialCost(this.rfq, channel);

    channel.materialCost = tempMaterialCost.label;

    const materialCost = tempMaterialCost.amount;
    const laborCost = this.getLaborCost(this.rfq);
    const returnDefectiveCost = this.getReturnDefectiveCost(this.rfq);
    const packageCost = this.getPackageCost(this.rfq);

    if (channel.retailPrice && channel.retailPrice > 0) {
      channel.costs = [];
      channel.totalCost = 0;

      channel.costs.push({
        label: 'Material Cost',
        amount: materialCost,
        type: 2,
      });
      channel.totalCost += materialCost;

      channel.costs.push({
        label: 'Shipping Cost',
        amount: shippingCost,
        type: 1,
      });
      channel.totalCost += shippingCost;

      if (this.incOverheadCost && this.overheadCost > 0) {
        channel.costs.push({
          label: 'Overhead Cost',
          amount: this.overheadCost,
          type: 3,
        });
        channel.totalCost += this.overheadCost;
      }

      channel.costs.push({
        label: 'Packaging Cost',
        amount: packageCost,
      });
      channel.totalCost += packageCost;

      channel.costs.push({
        label: 'Labor Cost',
        amount: laborCost,
      });
      channel.totalCost += laborCost;

      channel.costs.push({
        label: 'Return/Defective Cost',
        amount: returnDefectiveCost,
      });
      channel.totalCost += returnDefectiveCost;

      const fees = JSON.parse(channel.fees);
      fees.forEach((fee) => {
        if (fee.type == 1) {
          channel.costs.push({
            label: fee.label,
            amount: fee.amount,
            ctype: fee.type,
            camt: fee.amount,
          });
          channel.totalCost += fee.amount;
        } else if (fee.type == 5) {
          channel.costs.push({
            label: fee.label + '(' + fee.amount.toString() + '% of Material Cost)',
            amount: (materialCost * fee.amount) / 100,
            ctype: fee.type,
            camt: fee.amount,
          });
          channel.totalCost += (materialCost * fee.amount) / 100;
        } else {
          channel.costs.push({
            label: fee.label + '(' + fee.amount.toString() + '% of SRP)',
            amount: (channel.retailPrice * fee.amount) / 100,
            ctype: fee.type,
            camt: fee.amount,
          });
          channel.totalCost += (channel.retailPrice * fee.amount) / 100;
        }
      });

      const totalAmount = channel.retailPrice + channel.shippingPaid;
      channel.marginAmount = totalAmount - channel.totalCost;
      channel.marginPercent = (channel.marginAmount / totalAmount) * 100;
    }

    this.cd.detectChanges();
  }

  recomputeChannelMargin(channel) :void {
    if (!channel.costs || channel.costs.length == 0) {
      return this.computeChannelMargin(channel);
    }
    channel.totalCost = 0;
    channel.costs.forEach((cost) => {
      if (cost.ctype && cost.ctype == 2 && cost.camt) {
        cost.amount = (channel.retailPrice * cost.camt) / 100;
      }
      channel.totalCost += cost.amount;
    });

    const totalAmount = channel.retailPrice + channel.shippingPaid;
    channel.marginAmount = totalAmount - channel.totalCost;
    channel.marginPercent = (channel.marginAmount / totalAmount) * 100;
  }

  setMaterialCost(channel, cost) :void {
    cost.amount = this.rfq.unit_costs
      ? this.rfq.unit_costs[channel.materialCost]
      : 0;
    this.recomputeChannelMargin(channel);
  }

  getShippingCost(rfq :any, channel :any) :number {
    let shippingCost :number = 0;
    const shippingCarrierId = channel.shippingCarrierId;

    if (shippingCarrierId) {
      const carrier :any = this.shippingWeightPrices?.find(
        (row) => row.id == shippingCarrierId,
      );

      if (carrier && carrier.id) {
        if (carrier.packaging && carrier.packaging.length > 0) {
          // computer by packaging shipping cost
          const packaging = carrier.packaging.sort(
            (a, b) => a.max_volume - b.max_volume,
          );
          const volume =
            rfq.carton_length > 0
              ? rfq.carton_length * rfq.carton_width * rfq.carton_height
              : rfq.box_length * rfq.box_width * rfq.box_height;
          const pkg = packaging?.find((row) => volume <= row.max_volume);
          if (pkg && pkg.id) {
            shippingCost = parseFloat(pkg.price);
          }
        } else {
          // compute by weight shipping cost
          const shippingPrices :any = carrier.prices.sort(
            (a, b) => a.weight - b.weight,
          );
          const shipUnit :string = shippingPrices[0].unit.toLowerCase();

          const shippingWeight = this.getWeight(rfq.shipping_weight, shipUnit);

          const dimensionalWeight = this.getWeight(rfq.product_weight, shipUnit);
          const weight =
            shippingWeight > dimensionalWeight ? shippingWeight : dimensionalWeight;

          if (weight > 0) {
            let weightCost = shippingPrices?.find((row) => row.weight == weight);
            if (!weightCost) {
              weightCost = shippingPrices[shippingPrices.length - 1];
            }
            shippingCost =
              (parseFloat(weightCost.zone4) +
                parseFloat(weightCost.zone5) +
                parseFloat(weightCost.zone6) +
                parseFloat(weightCost.zone7) +
                parseFloat(weightCost.zone8)) /
              5;
          }
        }
      }
    }

    return shippingCost;
  }

  getWeight(weights :any, unit :string) {
    if (!weights) {
      return 0;
    }
    /**some sku weight is not array just single number
     * that triggers the issue on foreach. */
    if (!weights.length) {
      const weightSingleValue = weights;
      weights = [
        {
          wv: weightSingleValue,
          mu: unit,
        },
      ];
    }
    let totalWeight :number = 0;
    if (unit.trim().toLowerCase() == 'oz') {
      if (!weights.length) {
        weights.forEach((row :any) => {
          totalWeight +=
            row.mu.trim().toLowerCase() == 'oz'
              ? parseFloat(row.wv)
              : parseFloat(row.wv) * 16;
        });
      }
    } else {
      if (!weights.length) {
        weights.forEach((row :any) => {
          totalWeight +=
            row.mu.trim().toLowerCase() == 'oz'
              ? parseFloat(row.wv) / 16
              : parseFloat(row.wv);
        });
      }
    }

    return isNaN(totalWeight) ? 0 : Math.ceil(totalWeight);
  }

  getMaterialCost(rfq :any, channel :any) :any {
    const materialCost = {
      label: channel?.materialCost,
      amount: 0,
    };

    if (rfq.unit_costs === null) {
      return materialCost;
    }

    if (materialCost.label) {
      materialCost.amount =
        rfq.unit_costs && rfq.unit_costs[materialCost.label]
          ? rfq.unit_costs[materialCost.label]
          : 0;
    }

    if (materialCost.amount === 0) {
      if (rfq.unit_costs.computed_fifo > 0) {
        materialCost.amount = rfq.unit_costs.computed_fifo;
        materialCost.label = 'fifo';
      } else if (rfq.unit_costs.computed_weighted > 0) {
        materialCost.amount = rfq.unit_costs.computed_weighted;
        materialCost.label = 'weighted';
      } else if (rfq.unit_costs.computed_lastpo > 0) {
        materialCost.amount = rfq.unit_costs.computed_lastpo;
        materialCost.label = 'lastPo';
      }
    }

    if (materialCost.amount && materialCost.amount > 0) {
      materialCost.amount = (materialCost.amount * 100) / 100;
    }

    return materialCost;
  }

  getLaborCost(rfq :any) :number {
    let laborCost :number = 0;
    if (this.settings && this.settings.labor_costs) {
      const efficiencyScore = this.settings?.labor_costs?.find(
        (row) => row.score == rfq?.efficiency_score,
      );
      if (efficiencyScore) {
        laborCost = efficiencyScore?.cost;
      }
    }

    return laborCost;
  }

  getPackageCost(rfq :any) :number {
    let packageCost :number = 0;
    if (!rfq || !rfq.id) {
      return packageCost;
    }
    packageCost = rfq.package_cost;

    return packageCost || 0;
  }

  getReturnDefectiveCost(rfq :any) :number {
    let returnDefectiveCost :number = 0;
    if (!rfq || !rfq.id) {
      return returnDefectiveCost;
    }
    returnDefectiveCost =
      rfq?.unit_costs?.total_item_shipped > 0
        ? rfq?.unit_costs?.total_cost_defective_products /
          rfq?.unit_costs?.total_item_shipped
        : 0;

    return returnDefectiveCost || 0;
  }

  getRetailPrice(rfq :any) :number {
    let retailPrice :number = 0;
    if (rfq && rfq.retail_price) {
      retailPrice = rfq.retail_price;
    }

    if (!retailPrice) {
      retailPrice = rfq?.RfqActivePrices?.retail_price;
    }

    return retailPrice || 0;
  }

  getChannelRetailPrice(rfq :any, channel :any) :number {
    let retailPrice :number = 0;
    const activeChannelPrice = rfq?.RfqActivePrices?.active_channel_prices?.find(
      (acp) => acp.channel_id == channel.id,
    );
    if (activeChannelPrice && activeChannelPrice.id) {
      retailPrice = activeChannelPrice.unitPrice || 0;
    }
    if (!retailPrice) {
      retailPrice = this.getRetailPrice(rfq);
    }

    return retailPrice;
  }

  getChannelShippingCarrierId(rfq :any, channel :any) :any {
    let channelShippingCarrierId :any = null;
    const channelShippingCarrier = rfq?.RfqActivePrices?.channel_shipping?.find(
      (acsc) => acsc.channel_id == channel.id,
    );
    if (channelShippingCarrier && channelShippingCarrier.id) {
      channelShippingCarrierId = channelShippingCarrier.shipping_carrier_id;
    } else if (this.rfq.default_shipping && this.rfq.default_shipping > 0) {
      channelShippingCarrierId = this.rfq.default_shipping;
    } else if (channel.shipping_carrier_id > 0) {
      channelShippingCarrierId = channel.shipping_carrier_id;
    } else if (
      this.settings.default_shipping &&
      this.settings.default_shipping > 0
    ) {
      channelShippingCarrierId = this.settings.default_shipping;
    }

    return channelShippingCarrierId;
  }

  getChannelMaterialCost(rfq :any, channel :any) :any {
    let materialCost :any = null;
    const channelShippingCarrier = rfq?.RfqActivePrices?.channel_shipping?.find(
      (acsc) => acsc.channel_id == channel.id,
    );

    if (channelShippingCarrier && channelShippingCarrier.id) {
      materialCost = channelShippingCarrier?.material_cost;
    }

    if (!materialCost) {
      materialCost = this.defaultMaterialCost;
    }

    return materialCost;
  }

  getShippingPaid(rfq :any, channel :any) :number {
    let shippingPaid :number = 0;
    const latestSales = rfq?.latest_sales?.find(
      (ls) => ls.channel_id === channel.id,
    );

    if (latestSales && latestSales.id) {
      shippingPaid = latestSales.shippingAmount
        ? latestSales.shippingAmount / latestSales.total_order_quantity
        : 0;
    }

    return shippingPaid;
  }

  getShippingWeight(rfq :any) :any {
    let shippingWeight = null;

    try {
      rfq.shipping_weight = JSON.parse(rfq?.shipping_weight);
    } catch (e) {}

    if (!rfq.shipping_weight?.length) {
      shippingWeight = rfq?.shipping_weight + 'oz';
    } else {
      const weights = [];

      rfq?.shipping_weight?.forEach((item, index) => {
        weights.push(item?.wv + ' ' + item?.mu);
      });

      shippingWeight = weights.join(', ');
    }

    return shippingWeight;
  }
}
